import React, { useState, useEffect } from "react";
import {
  FileText,
  Download,
  Calendar,
  BarChart3,
  TrendingUp,
  DollarSign,
  Wallet,
  Activity,
  Search,
  Filter,
  Eye,
  ChevronRight,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
// Shared audit utilities (align with FacilityAgentReporting / ReportingCenter)
import {
  targetTypeInfo,
  schemaFormatters,
  normalizeTargetType,
  formatActor,
  renderTargetDetails,
  exportAllAuditLogsCsv,
  formatValue,
} from "../../utils/auditUtils";

const InvestorReporting = () => {
  const { getDisplayName } = useAuth();
  const [selectedReport, setSelectedReport] = useState("");
  const [dateRange, setDateRange] = useState("last30days");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [filterHolding, setFilterHolding] = useState("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("generate");
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);

  // Mock data for report types
  const reportTypes = [
    {
      id: "daily_profit_accrual",
      name: "Daily Profit Accrual Report",
      description: "Daily profit accrual tracking for your Sukuk investments",
      icon: Calendar,
      estimatedTime: "1-2 minutes",
    },
    {
      id: "investor_distribution",
      name: "Investor Distribution Report",
      description: "Sukuk profit distribution details and investor analysis",
      icon: BarChart3,
      estimatedTime: "2-3 minutes",
    },
    {
      id: "daily_position",
      name: "Daily Position Report",
      description:
        "Daily position report with master account, custody account, and securities information",
      icon: FileText,
      estimatedTime: "1-2 minutes",
    },
    {
      id: "transaction_file",
      name: "Transaction File",
      description:
        "Comprehensive transaction file report with trade details, settlement information, and counterparty data",
      icon: Activity,
      estimatedTime: "2-3 minutes",
    },
    {
      id: "trade_instruction_file",
      name: "Trade Instruction File",
      description:
        "Trade instruction file report with custody account details, trade types, and settlement information",
      icon: FileText,
      estimatedTime: "1-2 minutes",
    },
  ];

  // Mock data for holdings
  const holdings = [
    { id: "SUKUK-001", name: "Green Sukuk Infrastructure 2027", amount: 50000 },
    { id: "SUKUK-002", name: "Tech Development Sukuk", amount: 25000 },
    { id: "BOND-001", name: "Conventional Corporate Bond 2025", amount: 30000 },
  ];

  // Mock data for generated reports
  const reportsHistory = [
    {
      id: "RPT-INV-001",
      reportType: "Daily Profit Accrual Report",
      generatedDate: "2024-01-22T10:30:00Z",
      dateRange: "January 2024",
      parameters: {
        startDate: "2024-01-01",
        endDate: "2024-01-22",
        holdings: "All Holdings",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "1.8 MB",
    },
    {
      id: "RPT-INV-002",
      reportType: "Portfolio Performance Report",
      generatedDate: "2024-01-15T14:20:00Z",
      dateRange: "Q4 2023",
      parameters: {
        startDate: "2023-10-01",
        endDate: "2023-12-31",
        holdings: "All Holdings",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "2.4 MB",
    },
    {
      id: "RPT-INV-003",
      reportType: "Profit Distribution Report",
      generatedDate: "2024-01-10T09:15:00Z",
      dateRange: "2023",
      parameters: {
        startDate: "2023-01-01",
        endDate: "2023-12-31",
        holdings: "Sukuk Only",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "1.2 MB",
    },
    {
      id: "RPT-INV-004",
      reportType: "Investor Distribution Report",
      generatedDate: "2024-01-25T11:45:00Z",
      dateRange: "January 2025",
      parameters: {
        startDate: "2025-01-01",
        endDate: "2025-01-31",
        holdings: "Green Sukuk Infrastructure 2027",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "2.7 MB",
    },
    {
      id: "RPT-INV-008",
      reportType: "Daily Position Report",
      generatedDate: "2025-08-26T16:00:00Z",
      dateRange: "August 26, 2025",
      parameters: {
        startDate: "2025-08-26",
        endDate: "2025-08-26",
        holdings: "All Holdings",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "950 KB",
    },
    {
      id: "RPT-INV-009",
      reportType: "Transaction File",
      generatedDate: "2025-08-27T17:30:00Z",
      dateRange: "August 25-27, 2025",
      parameters: {
        startDate: "2025-08-25",
        endDate: "2025-08-27",
        holdings: "All Holdings",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "1.2 MB",
    },
    {
      id: "RPT-INV-010",
      reportType: "Trade Instruction File",
      generatedDate: "2025-08-08T14:30:00Z",
      dateRange: "August 5-8, 2025",
      parameters: {
        startDate: "2025-08-05",
        endDate: "2025-08-08",
        holdings: "All Holdings",
      },
      status: "completed",
      downloadUrl: "#",
      fileSize: "780 KB",
    },
  ];

  // Mock Investor Daily Profit Accrual Report Data
  const mockInvestorDailyProfitAccrualData = {
    reportTitle: "My Daily Profit Accrual Report - January 2024",
    reportPeriod: "January 1, 2024 - January 22, 2024",
    totalInvestment: 105000,
    totalAccruedProfit: 2438,
    portfolioReturn: 5.2,
    summary: {
      activeSukukHoldings: 75000,
      activeConventionalBonds: 30000,
      averageDailyAccrual: 110.82,
      totalDaysInPeriod: 22,
      projectedAnnualReturn: 40443,
    },
    holdingsBreakdown: [
      {
        bondName: "Green Sukuk Infrastructure 2027",
        bondType: "Sukuk",
        holdingAmount: 50000,
        annualRate: 5.25,
        dailyAccrual: 7.19,
        totalAccrued: 1582,
        maturityDate: "2027-12-31",
      },
      {
        bondName: "Tech Development Sukuk",
        bondType: "Sukuk",
        holdingAmount: 25000,
        annualRate: 4.75,
        dailyAccrual: 3.26,
        totalAccrued: 717,
        maturityDate: "2026-06-30",
      },
      {
        bondName: "Conventional Corporate Bond 2025",
        bondType: "Conventional",
        holdingAmount: 30000,
        annualRate: 4.5,
        dailyAccrual: 3.7,
        totalAccrued: 814,
        maturityDate: "2025-09-15",
      },
    ],
    dailyAccrualHistory: [
      { date: "2024-01-18", totalAccrual: 14.15, cumulativeAccrual: 2381.25 },
      { date: "2024-01-19", totalAccrual: 14.15, cumulativeAccrual: 2395.4 },
      { date: "2024-01-20", totalAccrual: 14.15, cumulativeAccrual: 2409.55 },
      { date: "2024-01-21", totalAccrual: 14.15, cumulativeAccrual: 2423.7 },
      { date: "2024-01-22", totalAccrual: 14.15, cumulativeAccrual: 2437.85 },
    ],
  };

  // Mock Investor Distribution Report Data
  const mockInvestorDistributionData = {
    reportTitle: "Sukuk Profit Distribution Report - January 2025",
    bondName: "Green Sukuk Infrastructure 2027",
    reportPeriod: "January 1, 2025 - January 31, 2025",
    summary: {
      totalInvestors: 156,
      totalProfitDistributed: 2458750,
      averageProfitPerInvestor: 15761.86,
      distributionDate: "2025-02-01",
      paymentStatus: "Completed",
    },
    investorHoldings: [
      {
        investorId: "INV-001",
        investorName: "Khazanah Nasional Berhad",
        investorType: "Government Linked Company",
        holdingAmount: 25000000,
        holdingPercentage: 25.0,
        profitDistributed: 614687.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-002",
        investorName: "EPF Investment Scheme",
        investorType: "Pension Fund",
        holdingAmount: 20000000,
        holdingPercentage: 20.0,
        profitDistributed: 491750.0,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-003",
        investorName: "KWAP Fund",
        investorType: "Pension Fund",
        holdingAmount: 15000000,
        holdingPercentage: 15.0,
        profitDistributed: 368812.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-004",
        investorName: "Amanah Raya Berhad",
        investorType: "Trust Company",
        holdingAmount: 10000000,
        holdingPercentage: 10.0,
        profitDistributed: 245875.0,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-005",
        investorName: "Permodalan Nasional Berhad",
        investorType: "Investment Company",
        holdingAmount: 8000000,
        holdingPercentage: 8.0,
        profitDistributed: 196700.0,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-006",
        investorName: "Great Eastern Takaful",
        investorType: "Insurance Company",
        holdingAmount: 7000000,
        holdingPercentage: 7.0,
        profitDistributed: 172112.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-007",
        investorName: "Maybank Islamic",
        investorType: "Islamic Bank",
        holdingAmount: 5000000,
        holdingPercentage: 5.0,
        profitDistributed: 122937.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-008",
        investorName: "CIMB Islamic Bank",
        investorType: "Islamic Bank",
        holdingAmount: 3000000,
        holdingPercentage: 3.0,
        profitDistributed: 73762.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-009",
        investorName: "Public Islamic Bank",
        investorType: "Islamic Bank",
        holdingAmount: 2000000,
        holdingPercentage: 2.0,
        profitDistributed: 49175.0,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
      {
        investorId: "INV-010",
        investorName: "Al Rajhi Banking",
        investorType: "Islamic Bank",
        holdingAmount: 5000000,
        holdingPercentage: 5.0,
        profitDistributed: 122937.5,
        paymentMethod: "Bank Transfer",
        paymentStatus: "Paid",
      },
    ],
    distributionBreakdown: [
      {
        investorType: "Government Linked Company",
        count: 12,
        totalHolding: 35000000,
        totalProfit: 860125.0,
      },
      {
        investorType: "Pension Fund",
        count: 25,
        totalHolding: 40000000,
        totalProfit: 983500.0,
      },
      {
        investorType: "Islamic Bank",
        count: 45,
        totalHolding: 15000000,
        totalProfit: 368812.5,
      },
      {
        investorType: "Insurance Company",
        count: 18,
        totalHolding: 7000000,
        totalProfit: 172112.5,
      },
      {
        investorType: "Investment Company",
        count: 32,
        totalHolding: 3000000,
        totalProfit: 74200.0,
      },
      {
        investorType: "Individual Investor",
        count: 24,
        totalHolding: 0,
        totalProfit: 0,
      },
    ],
  };

  // Mock Investor Daily Position Report Data
  const mockInvestorDailyPositionReportData = {
    reportTitle: "Daily Position Report - August 26, 2025",
    reportPeriod: "August 26, 2025",
    positionDate: "26/08/2025",
    positions: [
      {
        normalMasterNo: "M14011XXXXXX",
        normalMasterName: "XXXXXX",
        masterNo: "M14011XXXXXX",
        masterName: "XXXXXX",
        custodyAccountNo: "N14011XXXXXX",
        custodyAccountName: "XXXXXX",
        isin: "MYBUI2401718",
        scanCode: "UI240171",
        securityCode: "QUI240171",
        securityDescription: "GENM CAPITAL MTN 1826D 310529",
        securityType: "CONVENTIONAL BONDS",
        securityStatus: "Authorized",
        productType: "MEDIUM TERM NOTE",
        locationCode: "MLY",
        currency: "MYR",
        accountType: "Segregated",
        scriplessFlag: "Y",
        maturityDate: "05/31/2029",
        safekeepingPosition: "250000",
        walletNo: "——",
      },
      {
        normalMasterNo: "M14011YYYYYY",
        normalMasterName: "Global Investment Fund",
        masterNo: "M14011YYYYYY",
        masterName: "Global Investment Fund",
        custodyAccountNo: "N14011YYYYYY",
        custodyAccountName: "Global Investment Custody",
        isin: "MYBUI2401719",
        scanCode: "UI240172",
        securityCode: "QUI240172",
        securityDescription: "TECH DEVELOPMENT SUKUK 1827D 310530",
        securityType: "SUKUK",
        securityStatus: "Authorized",
        productType: "SUKUK WAKALAH",
        locationCode: "MLY",
        currency: "MYR",
        accountType: "Segregated",
        scriplessFlag: "Y",
        maturityDate: "05/30/2030",
        safekeepingPosition: "150000",
        walletNo: "——",
      },
      {
        normalMasterNo: "M14011ZZZZZZ",
        normalMasterName: "Pension Fund Malaysia",
        masterNo: "M14011ZZZZZZ",
        masterName: "Pension Fund Malaysia",
        custodyAccountNo: "N14011ZZZZZZ",
        custodyAccountName: "Pension Fund Custody",
        isin: "MYBUI2401720",
        scanCode: "UI240173",
        securityCode: "QUI240173",
        securityDescription: "GREEN INFRASTRUCTURE BOND 1828D 310531",
        securityType: "CONVENTIONAL BONDS",
        securityStatus: "Authorized",
        productType: "INFRASTRUCTURE BOND",
        locationCode: "MLY",
        currency: "MYR",
        accountType: "Segregated",
        scriplessFlag: "Y",
        maturityDate: "05/31/2031",
        safekeepingPosition: "300000",
        walletNo: "——",
      },
    ],
    summary: {
      totalPositions: 3,
      totalSafekeepingPosition: 700000,
      totalConventionalBonds: 550000,
      totalSukuk: 150000,
      uniqueAccounts: 3,
      uniqueSecurities: 3,
    },
  };

  // Mock Investor Transaction File Report Data
  const mockInvestorTransactionFileReportData = {
    reportTitle: "Transaction File Report - August 25-27, 2025",
    reportPeriod: "August 25-27, 2025",
    transactions: [
      {
        masterAccountNo: "M14011XXXXXX",
        masterAccountName: "XXXX",
        custodyAccountNo: "N14011XXXXXX",
        custodyAccountName: "XXXX",
        securityCode: "IDLADY",
        securityName: "DUTCH LADY MILK INDUSTRIES ORD",
        securityLocation: "MLY",
        currency: "MYR",
        tradeDate: "08/25/2025",
        settlementDate: "08/27/2025",
        settlementAmount: "25117.72",
        quantity: "900",
        systemRefNo: "S25H2611XX",
        clientRefNo: "L14011MACQUARIE",
        counterpartyCode: "MACQUARIE CAPITAL SECURITIES MALAYSIA SDN BHD",
        counterpartyName: "INTERFACE",
        cashAccountNo: "08/26/2025",
        inputter: "000927XX",
        inputDate: "08/26/2025",
        authorizer: "N",
        authorizeDate: "Y",
        billingTag: "DELIVERED PENDING SETTLEMENT",
        scriplessFlag: "——",
        tradeStatus: "——",
        walletNo: "——",
      },
      {
        masterAccountNo: "M14011YYYYYY",
        masterAccountName: "Global Investment Fund",
        custodyAccountNo: "N14011YYYYYY",
        custodyAccountName: "Global Investment Custody",
        securityCode: "IOICORP",
        securityName: "IOI CORPORATION BERHAD ORD",
        securityLocation: "MLY",
        currency: "MYR",
        tradeDate: "08/26/2025",
        settlementDate: "08/28/2025",
        settlementAmount: "45800.00",
        quantity: "1000",
        systemRefNo: "S25H2612YY",
        clientRefNo: "L14011CIMB",
        counterpartyCode: "CIMB INVESTMENT BANK BERHAD",
        counterpartyName: "CIMB INTERFACE",
        cashAccountNo: "08/27/2025",
        inputter: "000928YY",
        inputDate: "08/27/2025",
        authorizer: "Y",
        authorizeDate: "08/27/2025",
        billingTag: "SETTLED",
        scriplessFlag: "Y",
        tradeStatus: "COMPLETED",
        walletNo: "WAL001",
      },
      {
        masterAccountNo: "M14011ZZZZZZ",
        masterAccountName: "Pension Fund Malaysia",
        custodyAccountNo: "N14011ZZZZZZ",
        custodyAccountName: "Pension Fund Custody",
        securityCode: "GENM",
        securityName: "GENTING MALAYSIA BERHAD ORD",
        securityLocation: "MLY",
        currency: "MYR",
        tradeDate: "08/27/2025",
        settlementDate: "08/29/2025",
        settlementAmount: "32500.50",
        quantity: "1500",
        systemRefNo: "S25H2613ZZ",
        clientRefNo: "L14011MAYBANK",
        counterpartyCode: "MAYBANK INVESTMENT BANK BERHAD",
        counterpartyName: "MAYBANK INTERFACE",
        cashAccountNo: "08/28/2025",
        inputter: "000929ZZ",
        inputDate: "08/28/2025",
        authorizer: "Y",
        authorizeDate: "08/28/2025",
        billingTag: "PENDING SETTLEMENT",
        scriplessFlag: "Y",
        tradeStatus: "PENDING",
        walletNo: "WAL002",
      },
    ],
    summary: {
      totalTransactions: 3,
      completedTransactions: 1,
      pendingTransactions: 2,
      totalSettlementAmount: 103418.22,
      uniqueCounterparties: 3,
      uniqueSecurities: 3,
    },
  };

  // Mock Investor Trade Instruction File Report Data
  const mockInvestorTradeInstructionFileReportData = {
    reportTitle: "Trade Instruction File Report - August 5-8, 2025",
    reportPeriod: "August 5-8, 2025",
    tradeInstructions: [
      {
        tradeType: "B",
        custodyAccountNo: "C14011462751",
        scanCode: "5211",
        stampDuty: "Y",
        isinCode: "MYBUI2401719",
        clientRefNo: "TI2025080701",
        issTag: "N",
        tradeDate: "05/08/2025",
        valueDate: "07/08/2025",
        noOfShares: "1000",
        settlementAmount: "50000.00",
        counterpartyAdaCode: "CIMB001",
        clearingFee: "25.00",
        clearingFeeGst: "1.50",
        buySellPrice: "50.00",
        brokerageFee: "150.00",
        brokerageFeeGst: "9.00",
        ssaAccountNo: "SSA14011001",
        accruedInterest: "125.50",
        turnaroundFlag: "N",
        walletNo: "WAL001",
      },
      {
        tradeType: "S",
        custodyAccountNo: "C14011462752",
        scanCode: "5212",
        stampDuty: "N",
        isinCode: "MYBUI2401720",
        clientRefNo: "TI2025080702",
        issTag: "N",
        tradeDate: "06/08/2025",
        valueDate: "08/08/2025",
        noOfShares: "2500",
        settlementAmount: "87500.00",
        counterpartyAdaCode: "MAYBANK01",
        clearingFee: "43.75",
        clearingFeeGst: "2.63",
        buySellPrice: "35.00",
        brokerageFee: "262.50",
        brokerageFeeGst: "15.75",
        ssaAccountNo: "SSA14011002",
        accruedInterest: "89.25",
        turnaroundFlag: "Y",
        walletNo: "WAL002",
      },
    ],
    summary: {
      totalInstructions: 3,
      buyOrders: 1,
      sellOrders: 2,
      totalTradedValue: 137500.0,
      totalClearingFees: 68.75,
      totalBrokerageFees: 412.5,
      uniqueCounterparties: 2,
    },
  };

  const handleGenerateReport = () => {
    if (!selectedReport) {
      alert("Please select a report type");
      return;
    }

    setIsGenerating(true);

    // Simulate report generation
    setTimeout(() => {
      setIsGenerating(false);
      console.log({
        reportType: selectedReport,
        dateRange,
        customStartDate,
        customEndDate,
        filterHolding,
      });

      // Show report preview for Daily Profit Accrual Report
      if (selectedReport === "daily_profit_accrual") {
        setSelectedReportData(mockInvestorDailyProfitAccrualData);
        setShowReportModal(true);
      } else if (selectedReport === "investor_distribution") {
        setSelectedReportData(mockInvestorDistributionData);
        setShowReportModal(true);
      } else if (selectedReport === "daily_position") {
        setSelectedReportData(mockInvestorDailyPositionReportData);
        setShowReportModal(true);
      } else if (selectedReport === "transaction_file") {
        setSelectedReportData(mockInvestorTransactionFileReportData);
        setShowReportModal(true);
      } else if (selectedReport === "trade_instruction_file") {
        setSelectedReportData(mockInvestorTradeInstructionFileReportData);
        setShowReportModal(true);
      }
    }, 3000);
  };

  const handleViewReport = (report) => {
    if (
      report.reportType === "Daily Profit Accrual Report" ||
      report.reportType?.includes("Daily Profit Accrual")
    ) {
      setSelectedReportData(mockInvestorDailyProfitAccrualData);
      setShowReportModal(true);
    } else if (
      report.reportType === "Investor Distribution Report" ||
      report.reportType?.includes("Investor Distribution")
    ) {
      setSelectedReportData(mockInvestorDistributionData);
      setShowReportModal(true);
    } else if (
      report.reportType === "Daily Position Report" ||
      report.reportType?.includes("Daily Position")
    ) {
      setSelectedReportData(mockInvestorDailyPositionReportData);
      setShowReportModal(true);
    } else if (
      report.reportType === "Transaction File" ||
      report.reportType?.includes("Transaction File")
    ) {
      setSelectedReportData(mockInvestorTransactionFileReportData);
      setShowReportModal(true);
    } else if (
      report.reportType === "Trade Instruction File" ||
      report.reportType?.includes("Trade Instruction File")
    ) {
      setSelectedReportData(mockInvestorTradeInstructionFileReportData);
      setShowReportModal(true);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      completed: "badge-success",
      generating: "badge-warning",
      failed: "badge-error",
    };

    return (
      <span className={`badge ${statusClasses[status] || "badge-secondary"}`}>
        {status === "completed" ? "Ready" : status}
      </span>
    );
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Define tabs for navigation
  const tabs = [
    { id: "generate", name: "Generate Reports", icon: FileText },
    { id: "audit", name: "Audit Logs", icon: Activity },
  ];

  // Real audit logs state
  // Audit logs state (aligned with issuer ReportingCenter formatting)
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState(null);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLimit, setAuditLimit] = useState(20);
  const [auditPagination, setAuditPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [auditExporting, setAuditExporting] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (activeTab !== "audit") return;
    if (!user) return;
    setAuditLogsLoading(true);
    setAuditLogsError(null);
    let params = {
      actorRole: "investor",
      limit: auditLimit,
      page: auditLogsPage,
    };
    if (user) {
      if (user.isSubAccount) {
        if (user.id || user._id) params.actorId = user.id || user._id;
      } else {
        if (user.id || user._id) params.parentAccountId = user.id || user._id;
      }
    }
    import("axios").then(({ default: axios }) => {
      axios
        .get("/api/audit-logs", { params })
        .then((res) => {
          const items = res.data?.items || res.data || [];
          setAuditLogs(items);
          const pagination = res.data?.pagination || {
            page: auditLogsPage,
            limit: auditLimit,
            total: items.length,
            pages: Math.ceil(items.length / auditLimit),
          };
          setAuditPagination(pagination);
          setAuditLogsLoading(false);
        })
        .catch(() => {
          setAuditLogsError("Failed to load audit logs");
          setAuditLogsLoading(false);
        });
    });
  }, [activeTab, auditLogsPage, auditLimit, user]);

  // Audit Logs Tab Content
  // --- Audit Logs Table (Issuer style alignment) ---
  const getAuditCategoryBadge = (category) => {
    const categoryClasses = {
      REPORT_GENERATION: "bg-blue-100 text-blue-800",
      PAYMENT_MANAGEMENT: "bg-purple-100 text-purple-800",
      INVESTOR_MANAGEMENT: "bg-green-100 text-green-800",
      COMPLIANCE: "bg-yellow-100 text-yellow-800",
      AUTHENTICATION: "bg-red-100 text-red-800",
      SYSTEM: "bg-gray-100 text-gray-800",
    };
    return categoryClasses[category] || "bg-gray-100 text-gray-800";
  };

  const getResultBadge = (result) => {
    return (result || "").toLowerCase() === "success"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const handleExportAllAuditLogs = async () => {
    try {
      setAuditExporting(true);
      await exportAllAuditLogsCsv({
        role: "investor",
        filePrefix: "investor_audit_logs",
      });
    } finally {
      setAuditExporting(false);
    }
  };

  const openAuditModal = (log) => {
    setSelectedAuditLog(log);
    setShowAuditModal(true);
  };

  const closeAuditModal = () => {
    setShowAuditModal(false);
    setSelectedAuditLog(null);
  };

  const renderAuditLogsTab = () => (
    <div className="space-y-6">
      <div className="overflow-hidden overflow-x-auto bg-white border rounded-lg border-light-gray">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                System Audit Trail
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Comprehensive log of all investor actions, portfolio
                interactions, and transactional activities.
              </p>
              <div className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-blue-800 bg-blue-100 rounded">
                Actor role: Investor
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                disabled={auditLogsLoading || auditExporting}
                onClick={handleExportAllAuditLogs}
                className="btn-primary"
              >
                {auditExporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </>
                )}
              </button>
              {auditExporting && (
                <span className="text-xs text-gray-500">
                  Gathering all pages of audit logs...
                </span>
              )}
            </div>
          </div>
        </div>
        <table className="table">
          <thead className="table-header">
            <tr>
              <th className="table-header-cell">Timestamp</th>
              <th className="table-header-cell">User</th>
              <th className="table-header-cell">Action</th>
              <th className="table-header-cell">Category</th>
              <th className="table-header-cell">Result</th>

              <th className="table-header-cell">Details</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {auditLogsLoading ? (
              <tr>
                <td colSpan="7" className="py-8 text-center">
                  Loading...
                </td>
              </tr>
            ) : auditLogsError ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-red-500">
                  {auditLogsError}
                </td>
              </tr>
            ) : auditLogs.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-8 text-center">
                  No audit logs found.
                </td>
              </tr>
            ) : (
              auditLogs.map((log) => (
                <tr key={log._id || log.id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    {formatDateTime(log.timestamp || log.createdAt)}
                  </td>
                  <td className="table-cell">{formatActor(log.actorId)}</td>
                  <td className="table-cell">
                    <div className="text-sm font-medium text-gray-900">
                      {log.action}
                    </div>
                  </td>
                  <td className="table-cell">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAuditCategoryBadge(
                        log.module
                      )}`}
                    >
                      {log.module ? log.module.replace("_", " ") : ""}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultBadge(
                        log.status || log.result
                      )}`}
                    >
                      {(log.status || log.result || "").toLowerCase() ===
                      "success" ? (
                        <CheckCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      )}
                      {log.status || log.result || ""}
                    </span>
                  </td>

                  <td className="table-cell">
                    <button
                      className="p-1 text-primary-600 hover:text-primary-700"
                      onClick={() => openAuditModal(log)}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
            {/* Modal rendered outside rows for accessibility */}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {auditPagination.total > 0 && (
              <span>
                Showing{" "}
                {Math.min(
                  (auditPagination.page - 1) * auditPagination.limit + 1,
                  auditPagination.total
                )}{" "}
                to{" "}
                {Math.min(
                  auditPagination.page * auditPagination.limit,
                  auditPagination.total
                )}{" "}
                of {auditPagination.total} logs
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
              onClick={() => setAuditLogsPage((p) => Math.max(1, p - 1))}
              disabled={auditLogsLoading || auditPagination.page <= 1}
            >
              Prev
            </button>
            {Array.from({ length: Math.max(1, auditPagination.pages) })
              .slice(
                Math.max(0, auditPagination.page - 3),
                Math.max(0, auditPagination.page - 3) + 5
              )
              .map((_, idx) => {
                const pageNum = Math.max(1, auditPagination.page - 3) + idx;
                if (pageNum > auditPagination.pages) return null;
                const isActive = pageNum === auditPagination.page;
                return (
                  <button
                    key={pageNum}
                    className={`px-3 py-1.5 text-sm rounded border ${
                      isActive
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-700"
                    }`}
                    onClick={() => setAuditLogsPage(pageNum)}
                    disabled={auditLogsLoading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
              onClick={() =>
                setAuditLogsPage((p) =>
                  Math.min(auditPagination.pages || p, p + 1)
                )
              }
              disabled={
                auditLogsLoading ||
                (auditPagination.pages > 0 &&
                  auditPagination.page >= auditPagination.pages)
              }
            >
              Next
            </button>
            <select
              className="px-2 py-1 ml-2 text-sm border border-gray-300 rounded"
              value={auditLimit}
              onChange={(e) => {
                setAuditLogsPage(1);
                setAuditLimit(Number(e.target.value));
              }}
              disabled={auditLogsLoading}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {showAuditModal && selectedAuditLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  Audit Log Details
                </h2>
                <button
                  onClick={closeAuditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Timestamp:</span>{" "}
                  {formatDateTime(
                    selectedAuditLog.timestamp || selectedAuditLog.createdAt
                  )}
                </div>
                <div>
                  <span className="font-medium">User:</span>{" "}
                  {formatActor(selectedAuditLog.actorId)}
                </div>
                <div>
                  <span className="font-medium">Action:</span>{" "}
                  {selectedAuditLog.action}
                </div>
                <div>
                  <span className="font-medium">Category:</span>{" "}
                  {selectedAuditLog.module?.replace("_", " ") || ""}
                </div>
                <div>
                  <span className="font-medium">Result:</span>{" "}
                  {(selectedAuditLog.status || selectedAuditLog.result || "")
                    .toString()
                    .toUpperCase()}
                </div>
                <div>
                  <span className="font-medium">IP Address:</span>{" "}
                  {selectedAuditLog.ip || selectedAuditLog.ipAddress || ""}
                </div>
                <div>
                  <span className="font-medium">Details:</span>{" "}
                  {selectedAuditLog.details?.description || ""}
                </div>
                <div>
                  <span className="font-medium">Target:</span>{" "}
                  {(() => {
                    const rawType =
                      selectedAuditLog.targetType ||
                      selectedAuditLog.target?.type ||
                      "";
                    const rawTargetId =
                      selectedAuditLog.targetId !== undefined
                        ? selectedAuditLog.targetId
                        : selectedAuditLog.target?.id !== undefined
                        ? selectedAuditLog.target?.id
                        : selectedAuditLog.target?._id;
                    let targetId = "";
                    if (
                      typeof rawTargetId === "object" &&
                      rawTargetId !== null
                    ) {
                      targetId =
                        rawTargetId._id ||
                        rawTargetId.id ||
                        rawTargetId.code ||
                        rawTargetId.name ||
                        JSON.stringify(rawTargetId);
                    } else if (typeof rawTargetId !== "undefined") {
                      targetId = String(rawTargetId);
                    }
                    if (rawType && targetId) {
                      return (
                        <>
                          <span className="px-1 font-mono text-xs bg-gray-100 rounded">
                            {targetId}
                          </span>
                          <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {rawType}
                          </span>
                        </>
                      );
                    } else if (targetId) {
                      return (
                        <span className="px-1 font-mono text-xs bg-gray-100 rounded">
                          {targetId}
                        </span>
                      );
                    } else if (rawType) {
                      return (
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {rawType}
                        </span>
                      );
                    }
                    return "N/A";
                  })()}
                </div>
                {(() => {
                  const rawType =
                    selectedAuditLog.targetType ||
                    selectedAuditLog.target?.type ||
                    "";
                  const targetType = normalizeTargetType(rawType);
                  const rawTargetId =
                    selectedAuditLog.targetId !== undefined
                      ? selectedAuditLog.targetId
                      : selectedAuditLog.target?.id !== undefined
                      ? selectedAuditLog.target?.id
                      : selectedAuditLog.target?._id;
                  let targetId = "";
                  if (typeof rawTargetId === "object" && rawTargetId !== null) {
                    targetId =
                      rawTargetId._id ||
                      rawTargetId.id ||
                      rawTargetId.code ||
                      rawTargetId.name ||
                      JSON.stringify(rawTargetId);
                  } else if (typeof rawTargetId !== "undefined") {
                    targetId = String(rawTargetId);
                  }
                  if (targetType && targetId && targetTypeInfo[targetType]) {
                    return (
                      <div className="mt-1 text-xs text-gray-600">
                        <span className="font-semibold">
                          {targetTypeInfo[targetType].label}:
                        </span>{" "}
                        {targetTypeInfo[targetType].description}
                      </div>
                    );
                  }
                  return null;
                })()}
                {selectedAuditLog.target && (
                  <div className="mt-3">
                    <div className="font-medium text-gray-900">
                      Target Details
                    </div>
                    {(() => {
                      const t = selectedAuditLog.target;
                      const rawType =
                        selectedAuditLog.targetType ||
                        selectedAuditLog.target?.type ||
                        "";
                      const targetType = normalizeTargetType(rawType);
                      if (targetType && schemaFormatters[targetType]) {
                        return schemaFormatters[targetType](t);
                      }
                      const exclude = new Set([
                        "_id",
                        "__v",
                        "createdAt",
                        "updatedAt",
                        "password",
                        "secret",
                        "hash",
                      ]);
                      const entries = Object.entries(t).filter(
                        ([k]) => !exclude.has(k)
                      );
                      if (!entries.length)
                        return (
                          <div className="mt-1 text-xs text-gray-500">
                            No details available.
                          </div>
                        );
                      return (
                        <div className="mt-2">
                          <ul className="text-sm text-gray-700 list-disc list-inside">
                            {entries.map(([key, value]) => (
                              <li key={key}>
                                <span className="font-semibold">{key}:</span>{" "}
                                {formatValue(value)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {selectedAuditLog.details &&
                  Object.keys(selectedAuditLog.details).filter(
                    (k) => k !== "description"
                  ).length > 0 && (
                    <div className="mt-2">
                      <span className="font-medium">Additional Details:</span>
                      <ul className="list-disc list-inside">
                        {Object.entries(selectedAuditLog.details)
                          .filter(([k]) => k !== "description")
                          .map(([key, value]) => (
                            <li key={key}>
                              <span className="font-semibold">{key}:</span>{" "}
                              {formatValue(value)}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="space-y-8">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Reports
          </h1>
          <p className="mt-1 text-gray-600">
            Generate comprehensive reports and
            access detailed audit logs
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Generate Reports Tab */}
        {activeTab === "generate" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Report Selection */}
              <div className="lg:col-span-2">
                <div className="p-6 bg-white border rounded-lg border-light-gray">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Select Report Type
                  </h3>
                  <div className="space-y-3">
                    {reportTypes.map((report) => {
                      const Icon = report.icon;
                      return (
                        <label
                          key={report.id}
                          className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedReport === report.id
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="reportType"
                            value={report.id}
                            checked={selectedReport === report.id}
                            onChange={(e) => setSelectedReport(e.target.value)}
                            className="mt-1 mr-3"
                          />
                          <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {report.name}
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                              {report.description}
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              Estimated generation time: {report.estimatedTime}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Report Parameters */}
              <div className="space-y-6">
                {/* Date Range */}
                <div className="p-6 bg-white border rounded-lg border-light-gray">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Date Range
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last7days"
                        checked={dateRange === "last7days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 7 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last30days"
                        checked={dateRange === "last30days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 30 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last90days"
                        checked={dateRange === "last90days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 90 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="custom"
                        checked={dateRange === "custom"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Custom Range</span>
                    </label>

                    {dateRange === "custom" && (
                      <div className="mt-3 space-y-2">
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filters */}
                <div className="p-6 bg-white border rounded-lg border-light-gray">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Filters
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Holdings
                      </label>
                      <select
                        value={filterHolding}
                        onChange={(e) => setFilterHolding(e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="all">All Holdings</option>
                        {holdings.map((holding) => (
                          <option key={holding.id} value={holding.id}>
                            {holding.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="p-6 bg-white border rounded-lg border-light-gray">
                  <button
                    onClick={handleGenerateReport}
                    disabled={!selectedReport || isGenerating}
                    className="w-full btn-primary"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === "audit" && renderAuditLogsTab()}

        {/* Report Preview Modal */}
        {showReportModal && selectedReportData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-secondary-900">
                  {selectedReportData.reportTitle}
                </h2>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Report Header */}
              <div className="p-4 mb-6 rounded-lg bg-gray-50">
                {selectedReportData.tradeInstructions ? (
                  // Trade Instruction File Report Header
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Report Period
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedReportData.reportPeriod}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Instructions
                      </span>
                      <p className="text-lg font-semibold text-primary-600">
                        {selectedReportData.summary.totalInstructions}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Traded Value
                      </span>
                      <p className="text-lg font-semibold text-success-600">
                        MYR{" "}
                        {selectedReportData.summary.totalTradedValue.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Buy Orders
                      </span>
                      <p className="text-lg font-semibold text-blue-600">
                        {selectedReportData.summary.buyOrders}
                      </p>
                    </div>
                  </div>
                ) : selectedReportData.transactions ? (
                  // Transaction File Report Header
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Report Period
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedReportData.reportPeriod}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Transactions
                      </span>
                      <p className="text-lg font-semibold text-primary-600">
                        {selectedReportData.summary.totalTransactions}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Settlement Amount
                      </span>
                      <p className="text-lg font-semibold text-success-600">
                        MYR{" "}
                        {selectedReportData.summary.totalSettlementAmount.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Completed Transactions
                      </span>
                      <p className="text-lg font-semibold text-green-600">
                        {selectedReportData.summary.completedTransactions}
                      </p>
                    </div>
                  </div>
                ) : selectedReportData.positions ? (
                  // Daily Position Report Header
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Position Date
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedReportData.positionDate}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Positions
                      </span>
                      <p className="text-lg font-semibold text-primary-600">
                        {selectedReportData.summary.totalPositions}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Total Safekeeping Position
                      </span>
                      <p className="text-lg font-semibold text-success-600">
                        {selectedReportData.summary.totalSafekeepingPosition.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Unique Accounts
                      </span>
                      <p className="text-lg font-semibold text-purple-600">
                        {selectedReportData.summary.uniqueAccounts}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        {selectedReportData.bondName
                          ? "Bond Name"
                          : "Report Period"}
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedReportData.bondName ||
                          selectedReportData.reportPeriod}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Report Period
                      </span>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedReportData.reportPeriod}
                      </p>
                    </div>
                    {selectedReportData.totalInvestment !== undefined && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Total Investment
                        </span>
                        <p className="text-lg font-semibold text-primary-600">
                          RM{" "}
                          {selectedReportData.totalInvestment.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedReportData.totalAccruedProfit !== undefined && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Total Accrued Profit
                        </span>
                        <p className="text-lg font-semibold text-success-600">
                          RM{" "}
                          {selectedReportData.totalAccruedProfit.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedReportData.summary?.totalProfitDistributed !==
                      undefined && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Total Profit Distributed
                        </span>
                        <p className="text-lg font-semibold text-success-600">
                          RM{" "}
                          {selectedReportData.summary.totalProfitDistributed.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedReportData.portfolioReturn !== undefined && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Portfolio Return
                        </span>
                        <p className="text-lg font-semibold text-success-600">
                          +{selectedReportData.portfolioReturn}%
                        </p>
                      </div>
                    )}
                    {selectedReportData.summary?.totalInvestors !==
                      undefined && (
                      <div>
                        <span className="text-sm font-medium text-gray-500">
                          Total Investors
                        </span>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedReportData.summary.totalInvestors}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Summary Section */}
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Summary
                </h3>

                {/* Trade Instruction File Report Summary */}
                {selectedReportData.tradeInstructions && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Instructions
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.totalInstructions}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Buy Orders
                      </span>
                      <p className="text-sm font-semibold text-blue-600">
                        {selectedReportData.summary.buyOrders}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Sell Orders
                      </span>
                      <p className="text-sm font-semibold text-red-600">
                        {selectedReportData.summary.sellOrders}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Clearing Fees
                      </span>
                      <p className="text-sm font-semibold text-orange-600">
                        MYR{" "}
                        {selectedReportData.summary.totalClearingFees.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Brokerage Fees
                      </span>
                      <p className="text-sm font-semibold text-purple-600">
                        MYR{" "}
                        {selectedReportData.summary.totalBrokerageFees.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Unique Counterparties
                      </span>
                      <p className="text-sm font-semibold text-green-600">
                        {selectedReportData.summary.uniqueCounterparties}
                      </p>
                    </div>
                  </div>
                )}

                {/* Transaction File Report Summary */}
                {selectedReportData.transactions && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Transactions
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.totalTransactions}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Completed
                      </span>
                      <p className="text-sm font-semibold text-green-600">
                        {selectedReportData.summary.completedTransactions}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Pending
                      </span>
                      <p className="text-sm font-semibold text-orange-600">
                        {selectedReportData.summary.pendingTransactions}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Settlement Amount
                      </span>
                      <p className="text-sm font-semibold text-blue-600">
                        MYR{" "}
                        {selectedReportData.summary.totalSettlementAmount.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Unique Counterparties
                      </span>
                      <p className="text-sm font-semibold text-purple-600">
                        {selectedReportData.summary.uniqueCounterparties}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Unique Securities
                      </span>
                      <p className="text-sm font-semibold text-indigo-600">
                        {selectedReportData.summary.uniqueSecurities}
                      </p>
                    </div>
                  </div>
                )}

                {/* Daily Position Report Summary */}
                {selectedReportData.positions && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Positions
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.totalPositions}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Conventional Bonds
                      </span>
                      <p className="text-sm font-semibold text-blue-600">
                        {selectedReportData.summary.totalConventionalBonds.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Sukuk Holdings
                      </span>
                      <p className="text-sm font-semibold text-green-600">
                        {selectedReportData.summary.totalSukuk.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Unique Accounts
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.uniqueAccounts}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Unique Securities
                      </span>
                      <p className="text-sm font-semibold text-purple-600">
                        {selectedReportData.summary.uniqueSecurities}
                      </p>
                    </div>
                  </div>
                )}

                {/* Daily Profit Accrual Report Summary */}
                {selectedReportData.summary?.activeSukukHoldings !==
                  undefined && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Sukuk Holdings
                      </span>
                      <p className="text-sm font-semibold text-green-600">
                        RM{" "}
                        {selectedReportData.summary.activeSukukHoldings.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Conventional Bonds
                      </span>
                      <p className="text-sm font-semibold text-blue-600">
                        RM{" "}
                        {selectedReportData.summary.activeConventionalBonds.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Avg Daily Accrual
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        RM{" "}
                        {selectedReportData.summary.averageDailyAccrual.toFixed(
                          2
                        )}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Days in Period
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.totalDaysInPeriod}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Projected Annual Return
                      </span>
                      <p className="text-sm font-semibold text-success-600">
                        RM{" "}
                        {selectedReportData.summary.projectedAnnualReturn.toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {/* Investor Distribution Report Summary */}
                {selectedReportData.summary?.totalProfitDistributed !==
                  undefined && (
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Investors
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.totalInvestors}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Total Profit Distributed
                      </span>
                      <p className="text-sm font-semibold text-success-600">
                        RM{" "}
                        {selectedReportData.summary.totalProfitDistributed.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Avg Profit Per Investor
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        RM{" "}
                        {selectedReportData.summary.averageProfitPerInvestor.toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Distribution Date
                      </span>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedReportData.summary.distributionDate}
                      </p>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded">
                      <span className="text-xs font-medium text-gray-500">
                        Payment Status
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {selectedReportData.summary.paymentStatus}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Investor Holdings Table - for Investor Distribution Report */}
              {selectedReportData.investorHoldings && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Investor Holdings & Profit Distribution
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Investor ID
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Investor Name
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Investor Type
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Holding Amount
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Holding %
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Profit Distributed
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-center text-gray-500 uppercase">
                            Payment Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.investorHoldings.map(
                          (investor, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {investor.investorId}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {investor.investorName}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600">
                                {investor.investorType}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                RM {investor.holdingAmount.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                {investor.holdingPercentage.toFixed(2)}%
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                                RM {investor.profitDistributed.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {investor.paymentStatus}
                                </span>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Distribution Breakdown Table - for Investor Distribution Report */}
              {selectedReportData.distributionBreakdown && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Distribution by Investor Type
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Investor Type
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Count
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Total Holding
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Total Profit
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.distributionBreakdown.map(
                          (breakdown, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {breakdown.investorType}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                {breakdown.count}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                RM {breakdown.totalHolding.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                                RM {breakdown.totalProfit.toLocaleString()}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Holdings Breakdown Table - for Daily Profit Accrual Report */}
              {selectedReportData.holdingsBreakdown && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    My Holdings Breakdown
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Bond Name
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Type
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            My Holding
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Annual Rate
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Daily Accrual
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Total Accrued
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Maturity
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.holdingsBreakdown.map(
                          (holding, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {holding.bondName}
                              </td>
                              <td className="px-4 py-2 text-sm">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                    holding.bondType === "Sukuk"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {holding.bondType}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                RM {holding.holdingAmount.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                {holding.annualRate}%
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                RM {holding.dailyAccrual.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                                RM {holding.totalAccrued.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                {holding.maturityDate}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Daily Accrual History - for Daily Profit Accrual Report */}
              {selectedReportData.dailyAccrualHistory && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Recent Daily Accrual History
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Daily Accrual
                          </th>
                          <th className="px-4 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Cumulative Accrual
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.dailyAccrualHistory.map(
                          (day, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {day.date}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-900">
                                RM {day.totalAccrual.toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                                RM {day.cumulativeAccrual.toFixed(2)}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Daily Position Report Table - for Daily Position Report (Custodian Table Structure) */}
              {selectedReportData.positions && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Daily Position Details
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Normal Master No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Normal Master Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Master No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Master Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Custody Account No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Custody Account Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            ISIN
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Scan Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Description
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Type
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Status
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Bond Type
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Location Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Currency
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Account Type
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Scripless Flag
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Maturity Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Position Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Safekeeping Position
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Wallet No
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.positions.map((position, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.normalMasterNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.normalMasterName}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.masterNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.masterName}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.custodyAccountNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.custodyAccountName}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.isin}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.scanCode}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.securityCode}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.securityDescription}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.securityType}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.securityStatus}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.productType}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.locationCode}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.currency}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.accountType}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.scriplessFlag}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {position.maturityDate}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {selectedReportData.positionDate || ""}
                            </td>
                            <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                              {position.safekeepingPosition}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {position.walletNo}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Transaction File Table - for Transaction File Report (Custodian Table Structure) */}
              {selectedReportData.transactions && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Transaction File Details
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Master Account No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Master Account Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Custody Account No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Custody Account Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Security Location
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Currency
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Trade Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Settlement Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Settlement Amount
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Quantity
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            System Ref No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Client Ref No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Counterparty Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Counterparty Name
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Cash Acc No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Inputter
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Input Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Authorizer
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Authorize Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Billing Tag
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Scripless Flag
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Trade Status
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Wallet No
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.transactions.map(
                          (transaction, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.masterAccountNo}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.masterAccountName}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.custodyAccountNo}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.custodyAccountName}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.securityCode}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.securityName}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.securityLocation}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.currency}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.tradeDate}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.settlementDate}
                              </td>
                              <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                                {transaction.settlementAmount}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {transaction.quantity}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.systemRefNo}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.clientRefNo}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.counterpartyCode}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.counterpartyName}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.cashAccountNo}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.inputter}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.inputDate}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.authorizer}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.authorizeDate}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.billingTag}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.scriplessFlag}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {transaction.tradeStatus}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {transaction.walletNo}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Trade Instruction File Table - for Trade Instruction File Report (Custodian Table Structure) */}
              {selectedReportData.tradeInstructions && (
                <div className="mb-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Trade Instruction File Details
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Trade Type
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Custody Account No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Scan Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Stamp Duty
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            ISIN Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Client Ref No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            ISS Tag
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Trade Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Value Date
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            No Of Shares
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Settlement Amount
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Counterparty ADA Code
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Clearing Fee
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Clearing Fee GST
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Buy/Sell Price
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Brokerage Fee
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Brokerage Fee GST
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            SSA Account No
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-right text-gray-500 uppercase">
                            Accrued Interest
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Turnaround Flag
                          </th>
                          <th className="px-2 py-2 text-xs font-medium text-left text-gray-500 uppercase">
                            Wallet No
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {selectedReportData.tradeInstructions.map(
                          (instruction, index) => (
                            <tr
                              key={index}
                              className="border-t border-gray-200"
                            >
                              <td className="px-2 py-2 text-xs">
                                {instruction.tradeType}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.custodyAccountNo}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.scanCode}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {instruction.stampDuty}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.isinCode}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.clientRefNo}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {instruction.issTag}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {instruction.tradeDate}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {instruction.valueDate}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.noOfShares}
                              </td>
                              <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                                {instruction.settlementAmount}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.counterpartyAdaCode}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.clearingFee}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.clearingFeeGst}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.buySellPrice}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.brokerageFee}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.brokerageFeeGst}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.ssaAccountNo}
                              </td>
                              <td className="px-2 py-2 text-xs text-right text-gray-900">
                                {instruction.accruedInterest}
                              </td>
                              <td className="px-2 py-2 text-xs text-gray-900">
                                {instruction.turnaroundFlag}
                              </td>
                              <td className="px-2 py-2 font-mono text-xs text-gray-900">
                                {instruction.walletNo}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white rounded-md bg-primary-600 hover:bg-primary-700">
                  <Download className="inline w-4 h-4 mr-2" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorReporting;
